import React from 'react';
import { Box, Typography, keyframes } from '@mui/material';

// Blinking animation for validation errors
export const blinkAnimation = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5);
    border-color: #ef4444;
  }
  50% { 
    box-shadow: 0 0 8px 2px rgba(239, 68, 68, 0.8);
    border-color: #dc2626;
  }
`;

// Required field label component with red asterisk
export const RequiredLabel = ({ children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
    {children}
    <Typography
      component="span"
      sx={{
        color: '#ef4444',
        fontWeight: 700,
        fontSize: '1.1em',
        ml: 0.5,
        lineHeight: 1
      }}
    >
      *
    </Typography>
  </Box>
);

// Styles for error state with blinking effect
export const getFieldErrorSx = (hasError) => {
  if (!hasError) return {};
  
  return {
    '& .MuiOutlinedInput-root': {
      animation: `${blinkAnimation} 1s ease-in-out 3`,
      '& fieldset': {
        borderColor: '#ef4444 !important',
        borderWidth: '2px !important'
      },
      '&:hover fieldset': {
        borderColor: '#dc2626 !important'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#ef4444 !important'
      }
    },
    '& .MuiInputLabel-root': {
      color: '#ef4444 !important',
      fontWeight: 600
    },
    '& .MuiFormHelperText-root': {
      color: '#ef4444 !important',
      fontWeight: 500
    }
  };
};

// Styles for Select field error state with blinking effect
export const getSelectErrorSx = (hasError) => {
  if (!hasError) return {};
  
  return {
    animation: `${blinkAnimation} 1s ease-in-out 3`,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important',
      borderWidth: '2px !important'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dc2626 !important'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444 !important'
    }
  };
};

// Validate form data against required fields
export const validateForm = (formData, requiredFields) => {
  const errors = {};
  
  requiredFields.forEach(field => {
    const value = formData[field];
    if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
      errors[field] = true;
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Custom hook helper for form validation
export const useFormValidation = (initialErrors = {}) => {
  const [errors, setErrors] = React.useState(initialErrors);
  const [touched, setTouched] = React.useState({});

  const clearError = (field) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  };

  const setError = (field, hasError = true) => {
    if (hasError) {
      setErrors(prev => ({ ...prev, [field]: true }));
    } else {
      clearError(field);
    }
  };

  const markTouched = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const resetValidation = () => {
    setErrors({});
    setTouched({});
  };

  return {
    errors,
    setErrors,
    touched,
    clearError,
    setError,
    markTouched,
    resetValidation
  };
};

export default {
  RequiredLabel,
  getFieldErrorSx,
  getSelectErrorSx,
  validateForm,
  useFormValidation,
  blinkAnimation
};
